import CryptoJS from "crypto-es";
import moment from "moment-timezone";

import { IMqttConfiguration } from "../services/MQTT/IMqttConfiguration";

interface IParams extends IMqttConfiguration {
	key?: string | CryptoJS.lib.WordArray | undefined;
	dateStamp: string;
	regionName?: string;
	serviceName?: string;
	algorithm: string;
	amzDate: string;
}

const utils = {
	sign: (key: string | CryptoJS.lib.WordArray | undefined, msg: string) => {
		const hash = CryptoJS.HmacSHA256(msg, key);

		return hash.toString(CryptoJS.enc.Hex);
	},

	sha256: (msg: string) => {
		const hash = CryptoJS.SHA256(msg);

		return hash.toString(CryptoJS.enc.Hex);
	},

	getSignatureKey: (key: string | CryptoJS.lib.WordArray | undefined, dateStamp: string, regionName: string, serviceName: string) => {
		const kDate = CryptoJS.HmacSHA256(dateStamp, `AWS4${key as string}`);
		const kRegion = CryptoJS.HmacSHA256(regionName, kDate);
		const kService = CryptoJS.HmacSHA256(serviceName, kRegion);

		return CryptoJS.HmacSHA256("aws4_request", kService);
	},
};

export const getSignedUrl = (options: IMqttConfiguration): string => {
	const params = getParams(options);
	const { protocol, host, canonicalUri } = params;
	const signature = signString(params);

	let canonicalQuerystring = getCanonicalQueryString(params);

	canonicalQuerystring += "&X-Amz-Signature=" + signature;
	if (params.sessionKey) {
		canonicalQuerystring += "&X-Amz-Security-Token=" + encodeURIComponent(params.sessionKey);
	}

	return `${protocol}://${host}${canonicalUri}?${canonicalQuerystring}`;
};


const getParams = (options: IMqttConfiguration): IParams => {
	const time = moment.utc();
	const dateStamp = time.format("YYYYMMDD");

	return {
		...options,
		dateStamp,
		algorithm: "AWS4-HMAC-SHA256",
		amzDate: dateStamp + "T" + time.format("HHmmss") + "Z",
	};
};

const getCanonicalQueryString = (params: IParams): string => {
	const { accessKey, amzDate } = params;
	const credentialScope = getCredentialScope(params);

	return [
		"X-Amz-Algorithm=AWS4-HMAC-SHA256",
		"X-Amz-Credential=" + encodeURIComponent(accessKey + "/" + credentialScope),
		"X-Amz-Date=" + amzDate,
		"X-Amz-Expires=86400",
		"X-Amz-SignedHeaders=host",
	].join("&");
};

const getCredentialScope = (params: IParams): string => {
	const { dateStamp, region, service } = params;

	return `${dateStamp}/${region}/${service}/aws4_request`;
};

const getCanonicalRequest = (params: IParams): string => {
	const canonicalQuerystring = getCanonicalQueryString(params);
	const canonicalHeaders = "host:" + params.host;
	const payloadHash = utils.sha256("");

	return [
		params.method,
		params.canonicalUri,
		canonicalQuerystring,
		canonicalHeaders,
		"\nhost",
		payloadHash,
	].join("\n");
};

const getStringToSign = (params: IParams): string => {
	const { algorithm, amzDate } = params;
	const credentialScope = getCredentialScope(params);
	const canonicalRequest = getCanonicalRequest(params);

	return [
		algorithm,
		amzDate,
		credentialScope,
		utils.sha256(canonicalRequest),
	].join("\n");
};

const signString = (params: IParams): string => {
	const { secretKey, dateStamp, region, service } = params;

	const stringToSign = getStringToSign(params);
	const signingKey = utils.getSignatureKey(secretKey, dateStamp, region, service);

	return utils.sign(signingKey, stringToSign);
};
